import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import ClientService from '@/services/ClientService';
import type { ClientProfileRequest, BookingParticipantOptionResponse } from '@/types/client';
import { ClientListItem } from '@/types/client';
import type { ClientChildRequest } from '@/types/clientChildren';
import { ClientChildListItem } from '@/types/clientChildren';

export const useClientStore = defineStore('client', () => {
  // State
  const clients = ref<ClientListItem[]>([]);
  const currentClient = ref<ClientListItem | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const clientChildren = ref<ClientChildListItem[]>([]);
  const selectedClientChild = ref<ClientChildListItem | null>(null);
  const clientChildrenLoading = ref(false);
  const clientChildrenError = ref<string | null>(null);
  
  // Booking participant options (clients + children)
  const bookingParticipantOptions = ref<BookingParticipantOptionResponse[]>([]);
  const bookingParticipantOptionsLoading = ref(false);
  
  let tempChildIdCounter = -1;
  // Getters
  const activeClients = computed(() =>
    clients.value.filter((client) => client.isActive)
  );

  // Actions - Client CRUD
  const loadClients = async (): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      clients.value = await ClientService.getClients();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load clients';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadClient = async (clientId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      currentClient.value = await ClientService.getClientById(clientId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load client';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createClient = async (request: ClientProfileRequest): Promise<ClientListItem> => {
    loading.value = true;
    error.value = null;
    try {
      const newClient = await ClientService.createClient(request);
      clients.value.push(newClient);
      return newClient;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create client';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateClient = async (
    clientId: number,
    request: ClientProfileRequest
  ): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      const updatedClient = await ClientService.updateClient(clientId, request);

      const index = clients.value.findIndex((client) => client.clientId === clientId);
      if (index !== -1) {
        clients.value[index] = updatedClient;
      }

      if (currentClient.value?.clientId === clientId) {
        currentClient.value = updatedClient;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update client';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteClient = async (clientId: number): Promise<void> => {
    loading.value = true;
    error.value = null;
    try {
      await ClientService.deleteClient(clientId);
      clients.value = clients.value.filter((client) => client.clientId !== clientId);

      if (currentClient.value?.clientId === clientId) {
        currentClient.value = null;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete client';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const clearCurrentClient = (): void => {
    currentClient.value = null;
  };

  const loadClientChildren = async (parentClientId: number): Promise<void> => {
    clientChildrenLoading.value = true;
    clientChildrenError.value = null;
    try {
      clientChildren.value = await ClientService.getClientChildren(parentClientId);
    } catch (err) {
      clientChildrenError.value = err instanceof Error ? err.message : 'Failed to load client children';
      throw err;
    } finally {
      clientChildrenLoading.value = false;
    }
  };

  const createClientChild = async (request: ClientChildRequest): Promise<ClientChildListItem> => {
    const optimisticChild = ClientChildListItem.createFromRequest(tempChildIdCounter--, request);
    clientChildren.value = [optimisticChild, ...clientChildren.value];
    try {
      const savedChild = await ClientService.createClientChild(request);
      const tempIndex = clientChildren.value.findIndex(
        (child) => child.clientChildId === optimisticChild.clientChildId
      );
      if (tempIndex !== -1) {
        clientChildren.value[tempIndex] = savedChild;
      } else {
        clientChildren.value.unshift(savedChild);
      }
      return savedChild;
    } catch (err) {
      clientChildren.value = clientChildren.value.filter(
        (child) => child.clientChildId !== optimisticChild.clientChildId
      );
      throw err;
    }
  };

  const updateClientChild = async (
    clientChildId: number,
    request: ClientChildRequest
  ): Promise<ClientChildListItem> => {
    const index = clientChildren.value.findIndex(
      (child) => child.clientChildId === clientChildId
    );

    if (index === -1) {
      throw new Error('Client child not found');
    }

    const previousSnapshot = clientChildren.value[index].toResponse();
    clientChildren.value[index] = new ClientChildListItem({
      ...previousSnapshot,
      parentClientId: request.parentClientId,
      firstName: request.firstName,
      lastName: request.lastName,
      identityCode: request.identityCode,
      birthDate: request.birthDate,
      modifiedDate: new Date().toISOString()
    });

    try {
      const updatedChild = await ClientService.updateClientChild(clientChildId, request);
      clientChildren.value[index] = updatedChild;
      return updatedChild;
    } catch (err) {
      clientChildren.value[index] = new ClientChildListItem(previousSnapshot);
      throw err;
    }
  };

  const deleteClientChild = async (clientChildId: number): Promise<void> => {
    const index = clientChildren.value.findIndex(
      (child) => child.clientChildId === clientChildId
    );

    if (index === -1) {
      return;
    }

    const removedChild = clientChildren.value[index];
    clientChildren.value.splice(index, 1);

    try {
      await ClientService.deleteClientChild(clientChildId);
    } catch (err) {
      clientChildren.value.splice(index, 0, removedChild);
      throw err;
    }
  };

  const promoteClientChild = async (clientChildId: number): Promise<ClientListItem> => {
    const index = clientChildren.value.findIndex(
      (child) => child.clientChildId === clientChildId
    );

    if (index === -1) {
      throw new Error('Client child not found');
    }

    const removedChild = clientChildren.value[index];
    clientChildren.value.splice(index, 1);

    try {
      const newClient = await ClientService.promoteClientChild(clientChildId);
      clients.value.push(newClient);
      return newClient;
    } catch (err) {
      clientChildren.value.splice(index, 0, removedChild);
      throw err;
    }
  };

  const clearClientChildren = (): void => {
    clientChildren.value = [];
  };

  const loadBookingParticipantOptions = async (): Promise<void> => {
    bookingParticipantOptionsLoading.value = true;
    try {
      bookingParticipantOptions.value = await ClientService.getBookingParticipantOptions();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load booking participant options';
      throw err;
    } finally {
      bookingParticipantOptionsLoading.value = false;
    }
  };

  return {
    // State
    clients,
    currentClient,
    loading,
    error,
    clientChildren,
    selectedClientChild,
    clientChildrenLoading,
    clientChildrenError,
    bookingParticipantOptions,
    bookingParticipantOptionsLoading,

    // Getters
    activeClients,

    // Actions
    loadClients,
    loadClient,
    createClient,
    updateClient,
    deleteClient,
    clearCurrentClient,
    loadClientChildren,
    createClientChild,
    updateClientChild,
    deleteClientChild,
    promoteClientChild,
    clearClientChildren,
    loadBookingParticipantOptions
  };
});
